import type { ProductionTimelineEntryType } from '@aeris/contracts'

import {
  ART_STATUS_META,
  STAGE_META,
  type UniformArtStatus,
} from './production-stage-meta.tsx'
import { documentRef } from './sales-labels.ts'

/**
 * A frase de cada linha da linha do tempo do pedido (F171).
 *
 * O servidor guarda o mínimo (de → para, os campos que mudaram); a frase é
 * montada aqui, com os rótulos que a tela já usa — a etapa diz "Iniciar
 * impressão" na linha do tempo como diz na fila.
 */

const FIELD_LABELS: Readonly<Record<string, string>> = {
  dispatchDate: 'data de despacho',
  fabric: 'tecido',
  personalized: 'personalizado',
  logistics: 'logística',
  eventDate: 'data do evento',
  eventNote: 'evento',
  paymentMark: 'sinal',
  gradeChecked: 'grade conferida',
  notes: 'observações',
}

const isArtStatus = (value: unknown): value is UniformArtStatus =>
  typeof value === 'string' && value in ART_STATUS_META

const count = (value: unknown): number =>
  typeof value === 'number' ? value : 0

const strings = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : []

export type TimelineText = {
  readonly title: string
  readonly detail: string | null
}

export const timelineText = (
  entry: ProductionTimelineEntryType
): TimelineText => {
  switch (entry.type) {
    case 'comment':
      return { title: entry.body, detail: null }
    case 'stage': {
      const printers = entry.printers.map((printer) => printer.name).join(', ')
      return {
        title: `Etapa: ${STAGE_META[entry.to].label}`,
        detail:
          [
            entry.from === null ? null : `era ${STAGE_META[entry.from].label}`,
            printers === '' ? null : printers,
            entry.forced ? 'sem a arte aprovada' : null,
          ]
            .filter((part) => part !== null)
            .join(' · ') || null,
      }
    }
    case 'event':
      return eventText(entry.kind, entry.detail)
  }
}

const eventText = (
  kind: Extract<ProductionTimelineEntryType, { type: 'event' }>['kind'],
  detail: Readonly<Record<string, unknown>>
): TimelineText => {
  switch (kind) {
    case 'ficha': {
      const fields = strings(detail.fields).map(
        (field) => FIELD_LABELS[field] ?? field
      )
      return {
        title: 'Ficha alterada',
        detail: fields.length === 0 ? null : fields.join(', '),
      }
    }
    case 'grade':
      return {
        title: 'Grade salva',
        detail: `${String(count(detail.pieces))} peças`,
      }
    case 'personalizados':
      return {
        title: 'Personalizados salvos',
        detail: `${String(count(detail.rows))} nomes, ${String(count(detail.pieces))} peças`,
      }
    case 'arte': {
      const parts = [
        isArtStatus(detail.from) && isArtStatus(detail.to)
          ? `era ${ART_STATUS_META[detail.from].label}`
          : null,
        detail.designerChanged === true ? 'designer trocado' : null,
        typeof detail.artReady === 'boolean'
          ? `arte no Drive: ${detail.artReady ? 'sim' : 'não'}, Corel: ${detail.productionFileReady === true ? 'sim' : 'não'}`
          : null,
      ].filter((part) => part !== null)
      return {
        title: isArtStatus(detail.to)
          ? `Arte: ${ART_STATUS_META[detail.to].label}`
          : 'Arte alterada',
        detail: parts.length === 0 ? null : parts.join(' · '),
      }
    }
    case 'imagem-adicionada':
      return {
        title: 'Imagem adicionada',
        detail: typeof detail.caption === 'string' ? detail.caption : null,
      }
    case 'imagem-removida':
      return {
        title: 'Imagem tirada',
        detail: typeof detail.caption === 'string' ? detail.caption : null,
      }
    case 'impressoras': {
      const names = strings(detail.printers)
      return {
        title: 'Impressoras corrigidas',
        detail: names.length === 0 ? 'nenhuma' : names.join(', '),
      }
    }
    case 'link':
      return {
        title: 'Pedido criado pelo link do cliente',
        detail: null,
      }
    case 'ocorrencia':
      return {
        title:
          detail.kind === 'refazer'
            ? 'Refazer registrado'
            : 'Problema registrado',
        detail:
          typeof detail.description === 'string' ? detail.description : null,
      }
    case 'ocorrencia-resolvida':
      return {
        title: 'Ocorrência resolvida',
        detail:
          typeof detail.resolution === 'string' ? detail.resolution : null,
      }
    case 'repetido':
      return {
        title: 'Pedido repetido',
        detail: `do ${documentRef(
          'order',
          typeof detail.sourceNumber === 'number' ? detail.sourceNumber : null
        )}`,
      }
  }
}
